/**
 * Handles Cookies and LocalStorage in a GDPR friendly way.
 * Requires to specify categories for each entity.
 */
import * as SafeCookie from "../utils/SafeCookie";
import { PerSessionStorage, PerTabStorage } from "../Storage";
// These were defined by sitecore, do not change:
const NECESSARY = "necessary";
const FUNCTIONAL = "functional";
const PERFORMANCE = "performance";
const PROMOTIONAL = "promotional";
const SITECORE_ACCEPTED_COOKIE_CATEGORIES = "allowedCookieCategories";
const LOCAL_STORAGE_MAX_AGE = 365 * 2 * 24 * 60 * 60 * 1000;
export type GdprCategory = "necessary" | "functional" | "performance" | "promotional" | "Uncategorized";
// end of sitecore definitions.
type BackendType =
	| "C" // Cookie
	| "S" // SessionStorage
	| "L";
// LocalStorage;
const ALL_ALLOWED_GDPR_CATEGORIES = [NECESSARY, FUNCTIONAL, PERFORMANCE, PROMOTIONAL]; // "Uncategorized" is not part of this!

const COOKIE_CATEGORIES_COOKIE_NAME = "MShopCookieCategories";

/**
 * true: it was allowed by the user,
 * false: it was declined by the user,
 * undefined: the user has not yet decided. Should be treated as "false" in pretty much all cases.
 */
type Consent = boolean | null | undefined;

/**
 * The result of a cookie read operation.
 */
export type ReadResult = {
	isAllowed: Consent;
	actualResult: string | null | undefined;
};
export type BaseStorageFrontend = {
	/**
	 * Returns the value for the given name, plus an info, whether access was allowed.
	 */
	get: (name: string) => ReadResult;
	put: (
		name: string,
		value: string | null | undefined,
		category: GdprCategory,
		optCookieOptions?: Record<string, any> | null,
	) => boolean;
	remove: (name: string) => boolean;
};
export type StorageFrontend = {
	/**
	 * Simpler version of "get" that only returns the actual result.
	 */
	read: (name: string) => string | null | undefined;
	get: (name: string) => ReadResult;
	put: (
		name: string,
		value: string | null | undefined,
		category: GdprCategory,
		optCookieOptions?: Record<string, any> | null,
	) => boolean;
	remove: (name: string) => boolean;
};
const CookieStore: BaseStorageFrontend = {
	/**
	 * If you do not want to know if the acces was allowed or not, just call `cookie.read(...).actualResult`.
	 * It will be undefined if the access was denied.
	 */
	get(name: string): ReadResult {
		return readInternal(name, SafeCookie.get, "C", undefined);
	},

	/**
	 * Returns true, if the cookie was actually written.
	 * Throws Errors if you do something real bad like providing a invalid category.
	 * If cookieOptions is truthy, it will be used unchanged to call SafeCookie.set. Otherwise SafeCookie.makeCookieOptions() will be used.
	 */
	put: function (
		name: string,
		value: string | null | undefined,
		category: GdprCategory,
		optCookieOptions?: Record<string, any> | null,
	): boolean {
		const cookieOptions = optCookieOptions || SafeCookie.makeCookieOptions(null);

		const setFun = (name: string, value: string | null | undefined): boolean => {
			return SafeCookie.set(name, value, cookieOptions);
		};

		return writeInternal(name, value, category, "C", setFun);
	},

	/**
	 * This is always allowed.
	 * Returns true if the expiring went ok.
	 */
	remove: function (name: string): boolean {
		return SafeCookie.expire(name);
	},
};
// aka PerSessionStorage
const LocalStorage: BaseStorageFrontend = {
	get(name: string): ReadResult {
		return readInternal(name, PerSessionStorage.get, "L", "");
	},

	put: function (name: string, value: string | null | undefined, category: GdprCategory): boolean {
		return writeInternal(name, value, category, "L", PerSessionStorage.put);
	},
	remove: function (name: string) {
		return PerSessionStorage.remove(name);
	},
};
// aka PerTabStorage
const SessionStorage: BaseStorageFrontend = {
	get(name: string): ReadResult {
		return readInternal(name, PerTabStorage.get, "S", "");
	},

	put: function (name: string, value: string | null | undefined, category: GdprCategory): boolean {
		return writeInternal(name, value, category, "S", PerTabStorage.put);
	},
	remove: function (name: string) {
		return PerTabStorage.remove(name);
	},
};

function enhanceWithReadFunction(storeFrontend: BaseStorageFrontend): StorageFrontend {
	return {
		get: storeFrontend.get,
		put: storeFrontend.put,
		remove: storeFrontend.remove,
		read: function (name: string): string | null | undefined {
			return this.get(name).actualResult;
		},
	};
}

/**
 * Returns the category of the cookie, if it was ever set, otherwise undefined.
 */
const getCategoryByCookieName = function (
	cookieName: string,
	backendType: BackendType,
): GdprCategory | null | undefined {
	if (!cookieName) {
		console.warn(`Invalid cookie name: '${cookieName}'`);
		return undefined;
	}

	try {
		let map = getCookieCategoryCookie(backendType);

		if (!map || !map[cookieName]) {
			map = getLegacyCookieCategoryCookie();

			if (!map) {
				return undefined;
			}
		}

		const category = map[cookieName];
		gdprStorage.validateCategory(category);
		return category;
	} catch {
		return undefined;
	}
};

/**
 * The cookie Category is necessary for reading cookies according to law.
 * We only allow reading if the Category is accepted by the user.
 * The CookieCategoriesCookie is a map from cookie name to category.
 */
const addCookieCategory = function (cookieName: string, category: GdprCategory, backendType: BackendType): boolean {
	gdprStorage.validateCategory(category);
	const legacyMap = getLegacyCookieCategoryCookie();
	const map = getCookieCategoryCookie(backendType);
	const actualMap = map || legacyMap || {};
	actualMap[cookieName] = category;
	const putOk = PerSessionStorage.put(COOKIE_CATEGORIES_COOKIE_NAME + backendType, JSON.stringify(actualMap));

	if (!putOk || !legacyMap) {
		return putOk;
	}

	delete legacyMap[cookieName];

	if (Object.values(legacyMap).length === 0) {
		return PerSessionStorage.remove(COOKIE_CATEGORIES_COOKIE_NAME);
	}

	return PerSessionStorage.put(COOKIE_CATEGORIES_COOKIE_NAME, JSON.stringify(legacyMap));
};

type GdprStorageType = {
	cookie: StorageFrontend;
	localStorage: StorageFrontend;
	sessionStorage: StorageFrontend;
	now: () => Date;
	isCategoryAllowed: (arg0: GdprCategory, arg1: string, arg2: string) => Consent;
	validateCategory: (arg0: GdprCategory) => void;
	visibleForTesting: Record<string, any>;
	removeNotAllowedCookies: () => void;
	isLocalstorageExpired: (arg0: string, arg1: Date, arg2: number) => boolean;
	validateLocalstorage: () => void;
	NECESSARY: GdprCategory;
	FUNCTIONAL: GdprCategory;
	PROMOTIONAL: GdprCategory;
	PERFORMANCE: GdprCategory;
};
const gdprStorage: GdprStorageType = {
	cookie: enhanceWithReadFunction(CookieStore),
	// aka PerSessionStorage
	localStorage: enhanceWithReadFunction(LocalStorage),
	// aka PerTabStorage
	sessionStorage: enhanceWithReadFunction(SessionStorage),
	NECESSARY,
	FUNCTIONAL,
	PROMOTIONAL,
	PERFORMANCE,

	/**
	 * Just for easy mocking in tests.
	 */
	now: function (): Date {
		return new Date();
	},

	/**
	 * Throws an Error if the category is unknown.
	 */
	isCategoryAllowed: function (category: GdprCategory): Consent {
		this.validateCategory(category);

		// necessary cookies are always allowed:
		if (category === NECESSARY) {
			return true;
		}

		let allowedCategoriesString: string | null | undefined = SafeCookie.get(SITECORE_ACCEPTED_COOKIE_CATEGORIES);

		if (!allowedCategoriesString) {
			// `Allowed Cookie categories is not set. Will forbid everything including "${name}". ${debugInfo}`
			return undefined;
		}

		// Sitecore safes the cookie uriEncoded, so we need to account for that
		try {
			allowedCategoriesString = decodeURI(allowedCategoriesString);
		} catch {
			// do nothing
		}

		const allowedCategories = allowedCategoriesString?.split("|") ?? "";
		return allowedCategories.indexOf(category) > -1;
	},
	validateCategory: function (category: GdprCategory): void {
		if (!isCategoryValid(category)) {
			throw new Error(
				`The Category ${category} is not valid. Use one of: ${ALL_ALLOWED_GDPR_CATEGORIES.join(", ")}`,
			);
		}
	},
	visibleForTesting: {
		getCategoryByCookieName,
		addCookieCategory,
	},
	removeNotAllowedCookies: function (): void {
		const cookie = getCookieCategoryCookie("C");

		if (cookie) {
			for (const key in cookie) {
				const category = cookie[key];

				if (isCategoryValid(category) && !gdprStorage.isCategoryAllowed(category, "", "")) {
					SafeCookie.expire(key);
				}
			}
		}

		const sessionStorage = getCookieCategoryCookie("S");

		if (sessionStorage) {
			for (const key in sessionStorage) {
				const category = sessionStorage[key];

				if (isCategoryValid(category) && !gdprStorage.isCategoryAllowed(category, "", "")) {
					PerTabStorage.remove(key);
				}
			}
		}

		const localStorage = getCookieCategoryCookie("L");

		if (localStorage) {
			for (const key in localStorage) {
				const category = localStorage[key];

				if (isCategoryValid(category) && !gdprStorage.isCategoryAllowed(category, "", "")) {
					PerSessionStorage.remove(key);
				}
			}
		}
	},
	isLocalstorageExpired: function (key: string, date: Date, maxAge: number): boolean {
		const storageTimestamp: string | null | undefined =
			key.match("_timestamp") !== null ? PerSessionStorage.get(key) : PerSessionStorage.get(`${key}_timestamp`);

		if (storageTimestamp === null || storageTimestamp === undefined) {
			return false;
		}

		return date.getTime() - parseInt(storageTimestamp) >= maxAge;
	},

	/**
	 *  Delete localstorage entities if the storage key is
	 *  older than 24 months ( >= 24 ) to cpmply with the GDPR regulations.
	 */
	validateLocalstorage: function (): void {
		const localStorage = window.localStorage || getCookieCategoryCookie("L");
		const date = new Date();

		if (localStorage) {
			Object.keys(localStorage).forEach(key => {
				if (this.isLocalstorageExpired(key, date, LOCAL_STORAGE_MAX_AGE)) {
					if (PerSessionStorage.get(`${key}_timestamp`) !== null) {
						PerSessionStorage.remove(`${key}_timestamp`);
					}

					PerSessionStorage.remove(key);
				}
			});
		}
	},
};

/**
 * Within the grace period, reading is always allowed.
 * Writing is unaffected by the grace period.
 *
 * After the grace period is over, this code can be removed or just return false always here.
 */
const isGracePeriodActive = function () {
	const now = gdprStorage.now();
	const endOfGracePeriod = new Date(Date.UTC(2018, 9, 1, 0, 0, 0, 0)); // Oct 1st

	return now.getTime() < endOfGracePeriod.getTime();
};

const isCategoryValid = function (category: GdprCategory): boolean {
	return ALL_ALLOWED_GDPR_CATEGORIES.indexOf(category) !== -1;
};

const getCookieCategoryCookie = function (backendType: BackendType): Record<string, any> | null | undefined {
	const value = PerSessionStorage.get(COOKIE_CATEGORIES_COOKIE_NAME + backendType);

	try {
		if (value) {
			return JSON.parse(value);
		}

		return undefined;
	} catch {
		return undefined;
	}
};

const getLegacyCookieCategoryCookie = function (): Record<string, any> | null | undefined {
	const value = PerSessionStorage.get(COOKIE_CATEGORIES_COOKIE_NAME);

	try {
		if (value) {
			return JSON.parse(value);
		}

		return undefined;
	} catch {
		return undefined;
	}
};

const writeInternal = function (
	name: string,
	value: string | null | undefined,
	category: GdprCategory,
	backendType: BackendType,
	setFunction: (name: string, value: string | null | undefined) => boolean,
): boolean {
	if (isGracePeriodActive()) {
		addCookieCategory(name, category, backendType);
		return setFunction(name, value);
	}

	gdprStorage.validateCategory(category);
	const isCategoryAccepted = gdprStorage.isCategoryAllowed(category, name, "WRITE");

	if (isCategoryAccepted) {
		addCookieCategory(name, category, backendType);
		return setFunction(name, value);
	} else {
		// `The Category "${category}" was not accepted by the user. The cookie "${name}" will not be saved!`
		return false;
	}
};

const getCookieIgnoringCategory = (
	cookieName: string,
	retrieveFunction: (cookieName: string) => string | null | undefined,
) => {
	const actualResult: string | null | undefined = retrieveFunction(cookieName);
	return {
		isAllowed: true,
		actualResult,
	};
};

const readInternal = function (
	name: string,
	retrieveFunction: (name: string) => string | null | undefined,
	backendType: BackendType,
	defaultValue: string | null | undefined,
): ReadResult {
	if (isGracePeriodActive()) {
		return getCookieIgnoringCategory(name, retrieveFunction);
	}

	const storedCategory = getCategoryByCookieName(name, backendType);

	if (storedCategory) {
		const allowance = gdprStorage.isCategoryAllowed(storedCategory, name, "READ");

		if (!allowance) {
			return {
				isAllowed: allowance,
				actualResult: defaultValue,
			};
		}

		// category is allowed, so continue as if category can be ignored:
		return getCookieIgnoringCategory(name, retrieveFunction);
	}

	// `The grace period is over, and the cookie "${name}" did not have a category stored!`
	return {
		isAllowed: undefined,
		actualResult: defaultValue,
	};
};

addCookieCategory(COOKIE_CATEGORIES_COOKIE_NAME, NECESSARY, "C");
addCookieCategory(SITECORE_ACCEPTED_COOKIE_CATEGORIES, NECESSARY, "C");
addCookieCategory("JWT", NECESSARY, "C");
addCookieCategory("compressedJWT", NECESSARY, "C");
addCookieCategory("UserSettings", NECESSARY, "C");
// due to a bug (this should have been written as localstorage, not as cookie)
// we are removing the cookie on startup:
// FIXME SU: This should be removed at some point (although it won't hurt either way)
SafeCookie.expire(COOKIE_CATEGORIES_COOKIE_NAME);
export default gdprStorage;
